import { Badge } from '@/components/ui/Badge';
import {
  getAiSearchOverviewContext,
  getItemAiSearchContext,
} from '@/lib/ai-search-recommendations';
import type { AiSearchPlan, ItemDetail, ItemListItem } from '@/types';

type ContextItem = Pick<ItemListItem, 'primaryRole' | 'tags'> | Pick<ItemDetail, 'primaryRole' | 'tags'>;
type RecommendationContextVariant = 'summary' | 'compact' | 'detail';

interface AiSearchRecommendationContextProps {
  query: string;
  plan: AiSearchPlan;
  item?: ContextItem;
  variant?: RecommendationContextVariant;
  className?: string;
  aiIdPrefix?: string;
}

function renderSectionBadges(
  label: string,
  badgeVariant: 'default' | 'secondary' | 'outline',
  terms: Array<{ slug: string; name: string }>,
  aiIdPrefix: string,
) {
  if (terms.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2" data-ai-id={`${aiIdPrefix}-${label.toLowerCase().replace(/\s+/g, '-')}-section`}>
      <p
        className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500"
        data-ai-id={`${aiIdPrefix}-${label.toLowerCase().replace(/\s+/g, '-')}-label`}
      >
        {label}
      </p>
      <div className="flex flex-wrap gap-2" data-ai-id={`${aiIdPrefix}-${label.toLowerCase().replace(/\s+/g, '-')}-badges`}>
        {terms.map((term) => (
          <Badge
            key={term.slug}
            variant={badgeVariant}
            data-ai-id={`${aiIdPrefix}-${label.toLowerCase().replace(/\s+/g, '-')}-badge`}
          >
            {term.name}
          </Badge>
        ))}
      </div>
    </div>
  );
}

export function AiSearchRecommendationContext({
  query,
  plan,
  item,
  variant = 'summary',
  className = '',
  aiIdPrefix = 'ai-search-recommendation-context',
}: AiSearchRecommendationContextProps) {
  const isSummaryVariant = variant === 'summary';
  const overview = isSummaryVariant ? getAiSearchOverviewContext(plan) : null;
  const itemContext = item && !isSummaryVariant ? getItemAiSearchContext(item, plan) : null;
  const baseClassName = variant === 'compact'
    ? 'space-y-3 border border-sky-200 bg-sky-50/80 p-3'
    : 'space-y-4 border border-sky-200 bg-sky-50 p-4';
  const combinedClassName = [baseClassName, className].join(' ').trim();

  if (isSummaryVariant && !overview) {
    return null;
  }

  if (!isSummaryVariant && !itemContext) {
    return null;
  }

  if (isSummaryVariant && overview) {
    return (
      <div className={combinedClassName} data-ai-id={aiIdPrefix}>
        <div className="space-y-1" data-ai-id={`${aiIdPrefix}-copy`}>
          <p
            className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-700"
            data-ai-id={`${aiIdPrefix}-eyebrow`}
          >
            AI recommendation context
          </p>
          <h3 className="text-sm font-semibold text-slate-900" data-ai-id={`${aiIdPrefix}-title`}>
            Why these results match “{query}”
          </h3>
          <p className="text-sm leading-6 text-slate-700" data-ai-id={`${aiIdPrefix}-summary`}>
            {overview.summary}
          </p>
          {overview.adjacentSummary ? (
            <p className="text-sm leading-6 text-slate-600" data-ai-id={`${aiIdPrefix}-adjacent-summary`}>
              {overview.adjacentSummary}
            </p>
          ) : null}
        </div>

        <div className="grid gap-4 md:grid-cols-2" data-ai-id={`${aiIdPrefix}-sections`}>
          {renderSectionBadges('Required', 'default', overview.requiredTerms, aiIdPrefix)}
          {renderSectionBadges('Preferred v2 cues', 'outline', overview.preferredTerms, aiIdPrefix)}
          {renderSectionBadges('Avoid', 'secondary', overview.excludedTerms, aiIdPrefix)}
          {renderSectionBadges('Also consider', 'outline', overview.relatedRoleTerms, aiIdPrefix)}
        </div>
      </div>
    );
  }

  return (
    <div className={combinedClassName} data-ai-id={aiIdPrefix}>
      <div className="space-y-1" data-ai-id={`${aiIdPrefix}-copy`}>
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700"
          data-ai-id={`${aiIdPrefix}-eyebrow`}
        >
          Why this matches
        </p>
        <p className="text-sm leading-6 text-slate-700" data-ai-id={`${aiIdPrefix}-summary`}>
          {itemContext?.summary}
        </p>
        {itemContext?.adjacentSummary ? (
          <p className="text-xs leading-5 text-slate-600" data-ai-id={`${aiIdPrefix}-adjacent-summary`}>
            {itemContext.adjacentSummary}
          </p>
        ) : null}
      </div>

      {itemContext ? (
        <div className="space-y-3" data-ai-id={`${aiIdPrefix}-sections`}>
          {renderSectionBadges('Matched v2 cues', 'outline', itemContext.matchedTerms, aiIdPrefix)}
          {renderSectionBadges('Also consider', 'secondary', itemContext.relatedRoleTerms, aiIdPrefix)}
        </div>
      ) : null}
    </div>
  );
}
